/**
 * One submission, in full — what was asked, what was answered, what was
 * happening around it, and every verdict on it.
 *
 * Answers are rendered THROUGH `questionSnapshot`, never through the current
 * definition. That is the whole point of the snapshot: the wording and option
 * labels here are the ones this user actually saw, even if the definition has
 * been edited or the question deleted since.
 *
 * Deciding a case lives HERE rather than on the list row. Whether feedback has
 * been dealt with is a judgement that needs the answers and the verdicts in front
 * of you — a button on a row invites the call to be made from a status chip alone.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Tag } from 'sharedUi/Tag';
import { Button } from 'sharedUi/Button';
import { Message } from 'sharedUi/Message';
import { Dialog } from 'sharedUi/Dialog';
import { TextArea } from 'sharedUi/TextArea';
import FormField from '../../components/common/form-field';
import {
  feedbackReviewService, feedbackSubmissionService,
} from '../../services/feedback-submission.service';
import type {
  FeedbackAnswer, FeedbackQuestionSnapshot, FeedbackReview,
} from '../../types/feedback-submission.types';
import { useToastApi } from '../../hooks/use-toast-api';
import { api } from '../../services/api';
import { PERMISSIONS } from '../../constants/permissions';
import { statusSeverity } from './feedback-display';
import { SentimentMark, Stars } from './signal-cell';

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <div className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-ink-muted">
      {label}
    </div>
    <div className="text-sm text-ink">{children}</div>
  </div>
);

/** Render a stored answer using the wording — and now the symbols — captured at submit. */
function renderAnswer(q: FeedbackQuestionSnapshot | undefined, a: FeedbackAnswer) {
  const v = a.value;
  const type = (q?.questionType || '').toUpperCase();

  // Symbols, drawn from the SNAPSHOT's scale rather than today's definition, so
  // a rating answered out of 5 never redraws itself out of 10 later.
  if (type === 'RATING' && typeof v === 'number') {
    return <Stars value={v} scale={q?.ratingScale ?? 5} size="md" />;
  }
  if (type === 'SENTIMENT' && typeof v === 'string') {
    return (
      <span className="flex items-center gap-2">
        <SentimentMark value={v} size="md" />
        <span className="text-xs text-ink-muted">{v}</span>
      </span>
    );
  }

  if (Array.isArray(v)) {
    return v.length
      ? v.map((x) => q?.optionLabels?.[String(x)] ?? String(x)).join(', ')
      : '—';
  }
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number' && q?.ratingScale) return `${v} / ${q.ratingScale}`;
  if (typeof v === 'string') return q?.optionLabels?.[v] ?? v;
  return String(v ?? '—');
}

/* The exchange being judged, whatever AI surface it came from. Each component
   type names its own context keys (PROMPT says userInput/modelResponse, RAG says
   userQuery/finalResponse, EVAL says input/actualOutput…), so the reviewer's
   question — "what was asked, and what came back?" — is answered by whichever of
   these the definition captured. First match wins; the rest stay in the grid. */
const INPUT_KEYS = ['userInput', 'userQuery', 'userRequest', 'input', 'inputs'];
const OUTPUT_KEYS = ['modelResponse', 'finalResponse', 'response', 'agentOutput', 'actualOutput'];
const EXPECTED_KEYS = ['expectedOutput'];

const pickContext = (ctx: Record<string, unknown>, keys: string[]): [string, unknown] | null => {
  for (const k of keys) {
    const v = ctx[k];
    if (v !== undefined && v !== null && v !== '') return [k, v];
  }
  return null;
};

const asText = (v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v, null, 2));

const ExchangeBlock = ({ label, pair }: { label: string; pair: [string, unknown] | null }) => {
  if (!pair) return null;
  return (
    <div>
      <div className="mb-1 flex items-baseline gap-2">
        <span className="text-xs font-medium text-ink">{label}</span>
        <span className="font-mono text-[11px] text-gray-400">{pair[0]}</span>
      </div>
      <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-words rounded border border-gray-200 bg-gray-50 p-2 text-xs leading-relaxed text-ink dark:border-gray-700 dark:bg-gray-900/40">
        {asText(pair[1])}
      </pre>
    </div>
  );
};

const SubmissionDetail = ({ submissionId, onChanged, onBack, triage = true, actions }: {
  submissionId: string;
  onChanged?: () => void;
  /** When given, the detail renders as a page with its own back control. */
  onBack?: () => void;
  /** Offer the triage actions. False from the Reviews queue: a review row only
   *  exists because the feedback was already forwarded, so the SMEs are assigned
   *  and "Forward to SME" there is an action with nothing left to do. */
  triage?: boolean;
  /** Caller-supplied controls for the header — the Reviews queue puts "Record my
   *  verdict" here, so the verdict can be given while reading the feedback rather
   *  than only from the row it was opened from. */
  actions?: ReactNode;
}) => {
  const { toast, ok, fail } = useToastApi();

  const permKeys = useSelector((s: { auth?: { permissions?: string[] } }) => s.auth?.permissions || []);
  const permSet = useMemo(() => new Set(permKeys), [permKeys]);
  /** Deciding a case: mark it reviewed, or put it in front of an SME. */
  const canTriage = permSet.has(PERMISSIONS.FEEDBACK_SUBMISSION_MANAGE);
  /** Claiming/acting on a review row is a FEEDBACK_REVIEW write. */
  const canReview = permSet.has(PERMISSIONS.FEEDBACK_REVIEW_UPDATE);

  const [resolveNote, setResolveNote] = useState('');
  const [resolving, setResolving] = useState(false);

  const q = useQuery({
    queryKey: ['feedback-submission', submissionId],
    queryFn: () => feedbackSubmissionService.get(submissionId),
  });

  // Names for the reviewer column — the same minimal list the audit fields use.
  const usersQ = useQuery({
    queryKey: ['users-names-minimal'],
    queryFn: async () => {
      const res = await api.get<{ data: { _id: string; name: string }[] }>('/api/v1/users/names-minimal');
      return res.data.data || [];
    },
    staleTime: 10 * 60 * 1000,
  });
  const nameOf = useMemo(() => {
    const map = new Map((usersQ.data ?? []).map((u) => [u._id, u.name]));
    return (id?: string | null) => (id ? map.get(id) || id : '—');
  }, [usersQ.data]);

  const claimM = useMutation({
    mutationFn: (id: string) => feedbackReviewService.claim(id),
    onSuccess: () => { ok('Opened — this is now in review'); q.refetch(); onChanged?.(); },
    onError: (e) => fail(e),
  });

  const resolveM = useMutation({
    mutationFn: () => feedbackSubmissionService.markReviewed(submissionId, resolveNote),
    onSuccess: () => {
      ok('Marked as reviewed');
      setResolving(false); setResolveNote('');
      // Back to the list: the decision is made, and there is nothing further to
      // do on this record. Refresh the list behind us so it reflects the change.
      onChanged?.();
      onBack?.();
    },
    onError: (e) => fail(e),
  });

  /* No picker: the definition already says who reviews this and who decides, so
     forwarding sends it to exactly them. Choosing again on every submission would
     be re-answering a question already answered, and is how the wrong person gets
     picked. Routing somewhere the definition did not anticipate is still possible
     through the API, which takes explicit ids. */
  const forwardM = useMutation({
    mutationFn: () => feedbackSubmissionService.forward(submissionId),
    onSuccess: (updated) => {
      const n = updated?.assignedSmeIds?.length || 0;
      ok(n ? `Forwarded to ${n} SME${n > 1 ? 's' : ''}` : 'Forwarded for review');
      onChanged?.();
      onBack?.();
    },
    onError: (e) => fail(e),
  });

  // Back stays reachable even when the record will not load — otherwise a bad id
  // strands the user on a dead page with no way out but the browser.
  const back = onBack && (
    <Button icon="pi pi-arrow-left" label="Back" text size="small" onClick={onBack}
      data-testid="SubmissionDetail.Back" />
  );

  const d = q.data;

  /* Who a forward would actually reach: the definition's SMEs, minus anyone who
     already holds a row. Empty means there is nowhere to send it. */
  const alreadyAssigned = new Set(d?.assignedSmeIds || []);
  const toForward = (d?.suggestedSmes || []).filter((x) => !alreadyAssigned.has(x.userId));

  /* Triage is done once. NEW is the only state in which nothing has happened
     yet — forwarding makes it IN_REVIEW and marking reviewed makes it REVIEWED —
     so anything else means the decision has already been taken. */
  const acted = !!d && d.status !== 'NEW';

  if (q.isLoading) {
    return <div className="flex flex-col gap-2">{back}
      <div className="p-4 text-sm text-gray-500">Loading…</div></div>;
  }
  if (!d) {
    return <div className="flex flex-col gap-2">{back}
      <Message severity="error" className="w-full justify-start" text="Submission not found." /></div>;
  }

  const byCode = new Map((d.questionSnapshot || []).map((s) => [s.questionCode, s]));
  const ordered = [...(d.answers || [])].sort(
    (a, b) => (byCode.get(a.questionCode)?.displayOrder ?? 0) - (byCode.get(b.questionCode)?.displayOrder ?? 0),
  );
  const rc = (d.runtimeContext || {}) as Record<string, unknown>;
  const inPair = pickContext(rc, INPUT_KEYS);
  const outPair = pickContext(rc, OUTPUT_KEYS);
  const expPair = pickContext(rc, EXPECTED_KEYS);
  // Shown above in full; repeating them in the grid would print a whole answer
  // twice in a two-column cell.
  const promoted = new Set([inPair?.[0], outPair?.[0], expPair?.[0]].filter(Boolean) as string[]);
  const context = Object.entries(rc).filter(([k]) => !promoted.has(k));

  return (
    <div className="flex flex-col gap-4">
      {toast}

      {/* ------------------------------------------------------- page actions */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 pb-3 dark:border-gray-700">
        <div className="flex items-center gap-2">
          {back}
          <h3 className="text-sm font-semibold text-ink">
            Feedback
            <span className="ml-2 font-mono text-xs font-normal text-gray-400">
              {d.feedbackKey || '—'}
            </span>
          </h3>
        </div>
        {/* Triage happens ONCE. While the feedback is still NEW these are live
            choices; the moment one is taken they become the record of what was
            done, shown as text. Re-offering them afterwards would suggest the
            decision is revisable, and neither one is: a verdict cannot be
            un-asked for, and marking something reviewed twice means nothing. */}
        <div className="flex flex-wrap items-center gap-3">
        {actions}
        {canTriage && triage && !acted && (
          <div className="flex flex-wrap items-center gap-2">
            <Button label="Mark reviewed" icon="pi pi-check" size="small" outlined
              severity="success" onClick={() => setResolving(true)}
              data-testid="SubmissionDetail.MarkReviewed" />
            {/* Hidden when the definition names nobody — there is nowhere to
                forward it to, and a button that can only fail is worse than
                none. Mark reviewed is then the only move, which is correct. */}
            {/* Once only. Forwarding is what creates the review rows, and a second
                forward on the same feedback either duplicates them or silently
                does nothing — so the button goes the moment it has happened,
                whatever the definition's SME list looks like afterwards. */}
            {!d.forwardedAt && !!toForward.length && (
              <Button
                label={`Forward to SME${toForward.length > 1 ? `s (${toForward.length})` : ''}`}
                icon="pi pi-send" size="small" outlined
                onClick={() => forwardM.mutate()} disabled={forwardM.isPending}
                data-testid="SubmissionDetail.Forward" />
            )}
          </div>
        )}
        {acted && (
          <div className="text-right text-xs" data-testid="SubmissionDetail.Outcome">
            {/* The forwarding line lived here and is gone by request: the status
                chip below already says IN_REVIEW, and who it reached is on the
                review rows. Kept out of the header so the record starts with
                what was actually said. */}
            {d.reviewedAt && (
              <div className="text-gray-600 dark:text-gray-300">
                <i className="pi pi-check mr-1 text-[10px]" />
                Marked reviewed by {nameOf(d.reviewedBy)}
                <span className="text-gray-400"> · {new Date(d.reviewedAt).toLocaleString()}</span>
              </div>
            )}
            {d.reviewNote && (
              <div className="mt-0.5 italic text-ink-muted">“{d.reviewNote}”</div>
            )}
            {/* Neither field is set when an SME simply picked it up themselves —
                say so rather than leaving the corner blank. */}
            {!d.forwardedAt && !d.reviewedAt && (
              <span className="text-gray-400">Already in review — no triage action needed</span>
            )}
          </div>
        )}
        </div>
      </div>

      {/* --------------------------------------------------------- resolve */}
      <Dialog visible={resolving} onHide={() => setResolving(false)}
        header="Mark as reviewed" style={{ width: 520, maxWidth: '95vw' }} modal
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Cancel" severity="secondary" outlined
              onClick={() => setResolving(false)} disabled={resolveM.isPending} />
            <Button label="Mark reviewed" icon="pi pi-check"
              onClick={() => resolveM.mutate()} disabled={resolveM.isPending} />
          </div>
        }>
        <div className="flex flex-col gap-2">
          <p className="text-xs text-ink-muted">
            Use this when you have read the feedback and it needs nothing further.
            It settles the case without asking an SME. Read the answers first — a
            good rating can still carry a real problem in its comment.
          </p>
          <FormField label="Why? (optional)">
            <TextArea rows={3} value={resolveNote} className="w-full text-sm"
              placeholder="Already fixed in 3.2 · Working as designed · Not reproducible"
              onChange={(v: string) => setResolveNote(v)} />
          </FormField>
        </div>
      </Dialog>

      {/* ---------------------------------------------------------- identity */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Field label="Feedback">
          <span className="font-mono text-xs">{d.feedbackKey || '—'}</span>
        </Field>
        <Field label="Version">v{d.definitionVersion}</Field>
        <Field label="Component">
          <Tag value={d.component?.componentType || '—'} severity="info" />
        </Field>
        <Field label="Status">
          <Tag value={d.status} severity={statusSeverity(d.status) as never} />
        </Field>
        <Field label="Submitted by">{nameOf(d.submittedBy?.userId)}</Field>
        <Field label="Submitted at">
          {d.submittedAt ? new Date(d.submittedAt).toLocaleString() : '—'}
        </Field>
        <Field label="Component version">
          <span className="font-mono text-xs">{d.component?.componentVersion || '—'}</span>
        </Field>
      </div>

      {/* ----------------------------------------------------------- answers */}
      <section className="rounded border border-gray-200 p-3 dark:border-gray-700">
        <h4 className="mb-2 text-sm font-semibold text-ink">
          What they said
        </h4>
        <div className="flex flex-col gap-3">
          {ordered.map((a) => {
            const snap = byCode.get(a.questionCode);
            return (
              <div key={a.questionCode}>
                <div className="text-xs text-ink-muted">
                  {snap?.label || a.questionCode}
                </div>
                <div className="text-sm font-medium text-ink">
                  {renderAnswer(snap, a)}
                </div>
                {a.comment && (
                  <div className="mt-0.5 text-xs italic text-gray-600 dark:text-gray-300">
                    “{a.comment}”
                  </div>
                )}
              </div>
            );
          })}
          {!ordered.length && <div className="text-xs text-gray-500">No answers recorded.</div>}
        </div>
      </section>

      {/* ------------------------------------------------------- the exchange */}
      {(inPair || outPair || expPair) && (
        <section className="rounded border border-gray-200 p-3 dark:border-gray-700">
          <h4 className="mb-2 text-sm font-semibold text-ink">
            What was asked, and what came back
            <span className="ml-2 text-xs font-normal text-gray-400">
              · so the verdict is about something you can read
            </span>
          </h4>
          <div className="flex flex-col gap-3">
            <ExchangeBlock label="Input" pair={inPair} />
            <ExchangeBlock label="Output" pair={outPair} />
            <ExchangeBlock label="Expected" pair={expPair} />
          </div>
        </section>
      )}

      {/* ----------------------------------------------------------- context */}
      <section className="rounded border border-gray-200 p-3 dark:border-gray-700">
        <h4 className="mb-2 text-sm font-semibold text-ink">
          What was happening
          <span className="ml-2 text-xs font-normal text-gray-400">
            · captured automatically, per the definition
          </span>
        </h4>
        {context.length ? (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {context.map(([k, v]) => (
              <div key={k} className="text-xs">
                <span className="font-mono text-ink-muted">{k}</span>
                <div className={`break-words ${v === '[REDACTED]'
                  ? 'italic text-amber-600 dark:text-amber-400'
                  : 'text-ink'}`}>
                  {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-gray-500">
            No context declared — the answers arrive with no record of what they were about.
          </div>
        )}
        {d.sessionContext?.traceId && (
          <div className="mt-2 text-xs text-gray-400">
            trace <span className="font-mono">{d.sessionContext.traceId}</span>
            {d.sessionContext.route ? ` · ${d.sessionContext.route}` : ''}
          </div>
        )}
      </section>

      {/* ----------------------------------------------------------- reviews */}
      <section className="rounded border border-gray-200 p-3 dark:border-gray-700">
        <h4 className="mb-2 text-sm font-semibold text-ink">
          Reviews
          <span className="ml-2 text-xs font-normal text-gray-400">
            · the primary decides; everyone else is advisory
          </span>
        </h4>
        <div className="flex flex-col gap-2">
          {(d.reviews || []).map((r: FeedbackReview) => (
            <div key={r._id}
              className="rounded border border-gray-100 p-2 text-xs dark:border-gray-700">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="font-medium text-ink">{nameOf(r.reviewerUserId)}</span>
                {r.isPrimary && <Tag value="Primary" severity="warning" />}
                <Tag value={r.reviewStatus}
                  severity={r.reviewStatus === 'REVIEWED' ? 'success' : 'secondary'} />
                {r.reviewOutcome && <Tag value={r.reviewOutcome} severity="info" />}
                {r.actionRequired && <Tag value="Action required" severity="danger" />}
                {r.reviewStatus === 'PENDING' && canReview && (
                  <Button label="Open" size="small" text
                    onClick={() => claimM.mutate(r._id)} disabled={claimM.isPending} />
                )}
              </div>
              {r.reviewComments && (
                <div className="text-gray-700 dark:text-gray-300">{r.reviewComments}</div>
              )}
              {r.actionNotes && (
                <div className="mt-1 text-amber-700 dark:text-amber-400">→ {r.actionNotes}</div>
              )}
            </div>
          ))}
          {!(d.reviews || []).length && (
            <div className="text-xs text-amber-600 dark:text-amber-400">
              No reviewer was assigned — this feedback reaches nobody's inbox.
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SubmissionDetail;
